import React from "react";
import { DataCell } from "@/components/DataCell";
import { DataTable } from "@/components/DataTable";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import { FileDown, Image, Share, EyeIcon } from "lucide-react";
import { Congrats } from "@/components/Congrats";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/DataTable/States/Empty";
import { FormInputElements } from "./FormInputElements";
import { TableTabsMockImplementation } from "@/components/DataTable/TableTabs/MockImplementation";
import { ChartCard } from "@/components/ChartCard";
import {
  testCurrentData,
  testComparisonData,
} from "../../components/ChartCard/testData";

export const Components = () => {
  const { t } = useTranslation();

  const eventName = "Event Name";
  const eventNameKey = "eventName";
  const data = [
    {
      id: 1,
      eventName: "Picture This!",
      gameType: "Bot Event",
      startTime: "Feb 5, 2024 4:30 PM",
      audience: "All followers",
      chatChannel: "Game Room",
      isLive: true,
    },
    {
      id: 2,
      eventName: "TCU Bulk Message 2",
      gameType: "Live Event",
      startTime: "Feb 5, 2024 4:30 PM",
      audience: "All followers",
      chatChannel: "Live Event 2025",
      isLive: false,
    },
    {
      id: 3,
      eventName: "TCU Bulk Message 3",
      gameType: "Bot Event",
      startTime: "Feb 5, 2024 4:30 PM",
      audience: "All followers",
      chatChannel: "Game Room",
      isLive: false,
    },
    {
      id: 4,
      eventName: "TCU Bulk Message 4",
      gameType: "Bot Event",
      startTime: "Feb 5, 2024 4:30 PM",
      audience: "All followers",
      chatChannel: "Live Event 2025",
      isLive: false,
    },
    {
      id: 5,
      eventName: "TCU Bulk Message 5",
      gameType: "Bot Event",
      startTime: "Feb 5, 2024 4:30 PM",
      audience: "All followers",
      chatChannel: "Live Event 2025",
      isLive: false,
    },
    {
      id: 6,
      eventName: "Spring Showcase",
      gameType: "Live Event",
      startTime: "Feb 6, 2024 2:00 PM",
      audience: "All followers",
      chatChannel: "Live Event 2025",
      isLive: false,
    },
    {
      id: 7,
      eventName: "Welcome Session",
      gameType: "Bot Event",
      startTime: "Feb 6, 2024 3:30 PM",
      audience: "New members",
      chatChannel: "Game Room",
      isLive: false,
    },
    {
      id: 8,
      eventName: "Q&A Session",
      gameType: "Live Event",
      startTime: "Feb 7, 2024 1:00 PM",
      audience: "Premium members",
      chatChannel: "Live Event 2025",
      isLive: true,
    },
    {
      id: 9,
      eventName: "Tech Talk Tuesday",
      gameType: "Bot Event",
      startTime: "Feb 7, 2024 4:00 PM",
      audience: "All followers",
      chatChannel: "Game Room",
      isLive: false,
    },
    {
      id: 10,
      eventName: "Community Meetup",
      gameType: "Live Event",
      startTime: "Feb 8, 2024 11:00 AM",
      audience: "All followers",
      chatChannel: "Live Event 2025",
      isLive: false,
    },
    {
      id: 11,
      eventName: "Workshop Series",
      gameType: "Bot Event",
      startTime: "Feb 8, 2024 2:30 PM",
      audience: "Premium members",
      chatChannel: "Game Room",
      isLive: false,
    },
    {
      id: 12,
      eventName: "Product Launch",
      gameType: "Live Event",
      startTime: "Feb 9, 2024 10:00 AM",
      audience: "All followers",
      chatChannel: "Live Event 2025",
      isLive: true,
    },
    {
      id: 13,
      eventName: "Coding Challenge",
      gameType: "Bot Event",
      startTime: "Feb 9, 2024 3:00 PM",
      audience: "All followers",
      chatChannel: "Game Room",
      isLive: false,
    },
    {
      id: 14,
      eventName: "Network Night",
      gameType: "Live Event",
      startTime: "Feb 10, 2024 5:00 PM",
      audience: "All followers",
      chatChannel: "Live Event 2025",
      isLive: false,
    },
    {
      id: 15,
      eventName: "Weekend Recap",
      gameType: "Bot Event",
      startTime: "Feb 10, 2024 7:30 PM",
      audience: "All followers",
      chatChannel: "Game Room",
      isLive: false,
    },
  ];
  const columns: ColumnDef<(typeof data)[0]>[] = [
    {
      id: "eventName",
      accessorKey: "eventName",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: eventName,
      },
      header: ({ column }: { column: Column<(typeof data)[0]> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {eventName}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<(typeof data)[0]> }) => {
        return (
          <DataTable.TBodyCell>
            {row.getValue(eventNameKey)}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "gameType",
      accessorKey: "gameType",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: t("components.gameTypeComponent"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("components.gameTypeComponent")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<(typeof data)[0]> }) => {
        return (
          <DataTable.TBodyCell>{row.original.gameType}</DataTable.TBodyCell>
        );
      },
    },
    {
      id: "startTime",
      accessorKey: "startTime",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: t("components.startTimeComponent"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("components.startTimeComponent")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<(typeof data)[0]> }) => {
        return (
          <DataTable.TBodyCell>{row.original.startTime}</DataTable.TBodyCell>
        );
      },
    },
    {
      id: "audience",
      accessorKey: "audience",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: t("components.audienceComponent"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("components.audienceComponent")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<(typeof data)[0]> }) => {
        return (
          <DataTable.TBodyCell>{row.original.audience}</DataTable.TBodyCell>
        );
      },
    },
    {
      id: "chatChannel",
      accessorKey: "chatChannel",
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: t("components.chatChannelComponent"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("components.chatChannelComponent")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<(typeof data)[0]> }) => {
        return (
          <DataTable.TBodyCell>{row.original.chatChannel}</DataTable.TBodyCell>
        );
      },
    },
  ];

  return (
    <main>
      <h1 className="border-b p-3 text-center text-xl font-bold">
        {t("components.reusableComponents")}
      </h1>

      <ChartCard
        data={testCurrentData}
        comparisonData={testComparisonData}
        title="Student Enrollment Demo"
        description="Demo chart using mock data"
        yAxisLabel="Students"
        menuOptions={[]}
      />

      {/* Components */}
      <div className="p-3">
        <FormInputElements />
        {/* Table Component */}
        <div className="h-[1px] w-full bg-border"></div>

        {/* Congrats Component */}
        <div className="w-200" data-testid="congrats-component">
          <h2 className="m-3 text-lg font-semibold">
            {t("components.congratsComponent")}
          </h2>

          <Congrats
            icon={<Congrats.CheckIcon />}
            title={t("congrats.copies.createdGame.title")}
            avatars={
              <>
                <Congrats.Avatars count={6} />
                <Congrats.AvatarLabel>1,234 students</Congrats.AvatarLabel>
              </>
            }
            description={t("congrats.copies.createdGame.description", {
              dateTime: "Feb 23, 2025 6:30 PM",
            })}
            actions={
              <>
                <Congrats.CalendarActionButton>
                  {t("congrats.actions.calendar")}
                </Congrats.CalendarActionButton>
                <Congrats.ActionButtonLink route="nowhere">
                  {t("congrats.actions.done")}
                </Congrats.ActionButtonLink>
              </>
            }
          />
        </div>
        {/* End Congrats component */}
        <div className="h-[1px] w-full bg-border"></div>
        {/* Table Component */}
        <h2 className="z-[5] m-3 text-lg font-semibold">
          {t("components.tableComponent")}
        </h2>

        <div className="m-4 rounded-lg border shadow-lg">
          <DataTable
            topHeader={
              <>
                <DataTable.Title
                  title="Upcoming and Scheduled"
                  description="Quick summary of all your upcoming and scheduled campaigns"
                />
                <div className="flex items-center gap-2">
                  <DataTable.Filter />
                  <DataTable.HeaderActions
                    actions={[
                      {
                        key: "export-csv",
                        render: () => (
                          <>
                            <FileDown className="mr-2 h-4 w-4" />
                            Export to CSV
                          </>
                        ),
                        onClick: () => console.log("export-csv"),
                      },
                      {
                        key: "export-image",
                        render: () => (
                          <>
                            <Image className="mr-2 h-4 w-4" />
                            Export as image
                          </>
                        ),
                        onClick: () => console.log("export-image"),
                      },
                      {
                        key: "share",
                        render: () => (
                          <>
                            <Share className="mr-2 h-4 w-4" />
                            Share
                          </>
                        ),
                        onClick: () => console.log("share"),
                      },
                    ]}
                  />
                </div>
              </>
            }
            tableHeader={<DataTable.THeader />}
            tableBody={<DataTable.TBody />}
            pagination={
              <>
                <DataTable.PaginationLabel />
                <DataTable.PaginationControls />
              </>
            }
            isLoading={false}
            columns={columns}
            data={data}
          />
        </div>

        {/* Loading state */}
        <div className="h-[1px] w-full bg-border"></div>
        <h2 className="z-[5] m-3 text-lg font-semibold">
          {t("components.loadingState")}
        </h2>
        <DataTable isLoading={true} data={[]} />
        {/* Empty state */}
        <div className="h-[1px] w-full bg-border"></div>
        <h2 className="z-[5] m-3 text-lg font-semibold">
          {t("components.emptyState")}
        </h2>
        <DataTable
          data={[]}
          pagination={
            <>
              <DataTable.PaginationLabel />
              <DataTable.PaginationControls />
            </>
          }
          ariaLabelTableSection={t("components.ariaLabelTableSection")}
          ariaLabelTableContent={t("components.ariaLabelTableContent")}
          ariaLabelTablePagination={t("components.ariaLabelTablePagination")}
          emptyState={
            <EmptyState
              copy={t("dataTable.emptyStateCopy")}
              buttonTarget={() => console.log("buttonTarget")}
            />
          }
        />
        {/* Data cell component */}
        <div className="h-[1px] w-full bg-border"></div>
        <h2 className="m-3 text-lg font-semibold">
          {t("components.dataCellComponent")}
        </h2>

        <div className="mt-2 flex gap-4" id="datacell" role="region">
          <DataCell
            className="w-[250px]"
            title="Official Posts"
            middleValue="14"
            bottomValue="+2.1% from last cylce"
            icon={<EyeIcon />}
            isLoading={true}
          />
          <DataCell
            className="w-[250px]"
            title="Official Posts"
            middleValue="14"
            bottomValue="+2.1% from last cylce"
            icon={<EyeIcon />}
          />
        </div>
      </div>

      {/* Table Tabs */}
      <div className="h-[1px] w-full bg-border"></div>
      <h2 className="m-3 text-lg font-semibold">
        {t("components.tableTabs.title")}
      </h2>
      <div className="m-4">
        <TableTabsMockImplementation />
      </div>
    </main>
  );
};
